import { useState, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/axios";
import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// Rollback switch: set VITE_INVOICE_PDF_LEGACY_LINK=true at build time to
// restore the previous plain <a href> behaviour without touching code.
const USE_LEGACY_LINK = import.meta.env.VITE_INVOICE_PDF_LEGACY_LINK === "true";

interface InvoicePdfButtonProps {
  invoiceId: number;
  size?: "sm" | "default";
  children: ReactNode;
}

export function InvoicePdfButton({ invoiceId, size = "default", children }: InvoicePdfButtonProps) {
  const [loading, setLoading] = useState(false);

  if (USE_LEGACY_LINK) {
    return (
      <a
        href={`/api/invoices/${invoiceId}/pdf`}
        target="_blank"
        rel="noreferrer"
        className={cn(buttonVariants({ variant: "outline", size }))}
      >
        {children}
      </a>
    );
  }

  // Fetched through the shared axios instance so an expired access cookie is
  // refreshed transparently (a direct link would just hit a 401).
  const handleClick = async () => {
    // Open synchronously, inside the click, to avoid popup blockers.
    const win = window.open("", "_blank");
    setLoading(true);
    try {
      const res = await api.get<Blob>(`/invoices/${invoiceId}/pdf`, {
        responseType: "blob",
        timeout: 60_000,
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (win) {
        win.location.href = url;
      } else {
        window.open(url, "_blank");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      win?.close();
      toast.error("Impossible de générer le PDF de la facture. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button type="button" variant="outline" size={size} onClick={handleClick} disabled={loading}>
      {loading ? <Loader2 size={13} className="animate-spin" /> : children}
    </Button>
  );
}
